import React, { useMemo, useState } from 'react'

import axios from 'axios'
import CSS from 'csstype'
import _find from 'lodash/find'
import Image from 'next/legacy/image'
import { useRouter } from 'next/router'

import { useGenerateSampleContractDocsMutation } from '../../services/sampleContractDocs'
import { GenerateApplicationDocs } from './GenerateApplicationDocs'
import { useAppDispatch } from '@/client/hooks'
import { useAlertsState } from '@/components/Alertsv2/useAlertState'
import { ProfileTypes } from '@/constants/applicationProfile'
import {
  DOCUMENT_CATEGORY,
  DOCUMENT_OPTIONS,
  DOCUMENT_SOURCE,
  DOCUMENT_TAG,
  DOCUMENT_TYPE,
} from '@/constants/documents'
import { DownloadSectionModal } from '@/features/DealSummary/DownloadSectionModal'
import dropDownMenuItems, { DownloadOptionType } from '@/features/DealSummary/dropDownMenuItems'
import { openFilesInMaxViewer } from '@/features/Documents/helper'
import { downloadFile } from '@/features/FileManager/utilities'
import { useGetApplicationQuery } from '@/graphql/services/application'
import {
  useGetApplicationOfferCalcsQuery,
  useGetApplicationValuationsCalcsQuery,
} from '@/graphql/services/applicationCalcs'
import { useGetSsnUnmaskedQuery } from '@/services/applicationProfile'
import {
  useDocumentUploadFinishMutation,
  useDocumentUploadStartMutation,
  useListUserDocumentQuery,
} from '@/services/documents'
import { useUploadFileMutation } from '@/services/s3'
import { useGetSnapshotsQuery } from '@/services/snapshot'
import { useGetTradelinesByApplicationQuery } from '@/services/tradelines'
import { useGetLoggedUserQuery } from '@/services/user'
import {
  useListApplicationProfilesQuery,
  useListNonApplicantsQuery,
} from '@/services/v2-application/applicationProfile'
import { UserDocument } from '@/types/unlock'
import { FEATURES_LIST } from '@/utils/features/featuresList'
import { useFeatureFlag } from '@/utils/features/splitSdk'
import useShouldHideDTI from '@/utils/useShouldHideDTI'

const disableLinkStyle: CSS.Properties = {
  pointerEvents: 'none',
  cursor: 'default',
  textDecoration: 'none',
  opacity: 0.5,
}

interface IDownloadsSection {
  downloadingDoc?: (downloading: boolean) => void
  showDropdown: boolean
  setShowDropdown: React.Dispatch<React.SetStateAction<boolean>>
  setIsHovering: React.Dispatch<React.SetStateAction<boolean>>
  isLocked?: boolean
}

export function DownloadsSection({
  downloadingDoc,
  showDropdown,
  setShowDropdown,
  setIsHovering,
  isLocked = false,
}: IDownloadsSection) {
  const router = useRouter()
  const dispatch = useAppDispatch()

  const [disabledMessage, setDisabledMessage] = useState<string>(null)
  const [documentUploadStart] = useDocumentUploadStartMutation()
  const [documentUploadFinish] = useDocumentUploadFinishMutation()
  const [uploadFile] = useUploadFileMutation()
  const shouldHideDTI = useShouldHideDTI()
  const { alerts } = useAlertsState()

  const lifeOfLoanFloodZoneCertFeatureFlag = useFeatureFlag(
    FEATURES_LIST.LIFE_OF_LOAN_FLOOD_ZONE_CERT
  )

  const [showingModal, setShowingModal] = useState(false)
  const [modalData, setModalData] = useState<{ option: DownloadOptionType; docs: UserDocument[] }>()
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null)

  const { id } = router.query

  const { data: application } = useGetApplicationQuery(
    { externalId: id as string },
    {
      skip: !id,
    }
  )
  const { data: applicationProfiles } = useListApplicationProfilesQuery(application?.uuid, {
    skip: !application?.uuid,
  })
  const applicant = _find(applicationProfiles?.results, { position: 1 })
  const coApplicant = _find(applicationProfiles?.results, { position: 2 })
  const { data: ApplicantSSN } = useGetSsnUnmaskedQuery(applicant?.profile?.uuid, {
    skip: !applicant || !showDropdown,
  })

  const { data: CoApplicantSSN } = useGetSsnUnmaskedQuery(coApplicant?.profile?.uuid, {
    skip: !coApplicant || !showDropdown,
  })
  const { data: tradelinesPage } = useGetTradelinesByApplicationQuery(application?.uuid, {
    skip: !application,
  })

  const { data: applicationValuationsCalcsResponse } = useGetApplicationValuationsCalcsQuery(
    { externalId: application?.externalId },
    {
      skip: !application,
    }
  )

  const { data: nonApplicants } = useListNonApplicantsQuery(application?.uuid, {
    skip: !application?.uuid,
  })

  const nonDisclosureContacts = nonApplicants?.results
    ?.filter(
      (applicationParty) =>
        applicationParty?.profile_type === ProfileTypes.NOO ||
        applicationParty?.profile_type === ProfileTypes.CO_OWNER
    )
    .sort((a, b) => a.id - b.id)

  const applicationValuationsCalcs = useMemo(
    () => applicationValuationsCalcsResponse?.application?.valuationsCalculations,
    [applicationValuationsCalcsResponse]
  )

  const tradelines = tradelinesPage?.results

  const { data: user } = useGetLoggedUserQuery()
  const { data: snapshots } = useGetSnapshotsQuery(
    {
      active: true,
      application__id: application?.id,
    },
    { skip: !application || !showDropdown }
  )

  const { data: applicationOfferResponse } = useGetApplicationOfferCalcsQuery(
    { externalId: application?.externalId },
    {
      skip: !application,
    }
  )

  const applicationOffer = useMemo(
    () => applicationOfferResponse?.application?.offerCalculations,
    [applicationOfferResponse]
  )

  const hasOffer = applicationOffer?.selectedOffer?.offers != null

  const { data: userDocs } = useListUserDocumentQuery(String(applicant?.id), {
    skip: !applicant,
  })

  const [generateSampleContractDocs] = useGenerateSampleContractDocsMutation()

  const subStagesAllowed = [
    'pending-qualification',
    'pending-app-submit',
    'pending-doc-upload',
    'pending-file-setup',
    'pending-condl-approval-uw',
  ]

  const createdAtAfterLimit = application
    ? new Date(application.createdAt) >= new Date(Date.UTC(2022, 7, 3, 13, 0, 0, 0))
    : false
  const subStageBetweenFlag = subStagesAllowed.includes(application?.applicationState || '')

  const showIEButton = createdAtAfterLimit || subStageBetweenFlag
  const showICSButton = createdAtAfterLimit || !subStageBetweenFlag

  if (!application || !tradelinesPage) return null

  const payoffs = (tradelines || []).filter(
    (tradeline) =>
      tradeline.is_payoff && tradeline.payoff_included && tradeline.payoff_amount != '0.00'
  )

  const property = application?.property

  const getOwnershipType = (titleVesting: string) => {
    if (titleVesting.includes('trust')) return 'trust'
    if (titleVesting.includes('LLC')) return 'entity'

    return 'natural persons'
  }

  const getPropertyPlaceIncorporation = (state: string, legalDescription: string) => {
    if (state != 'MI') return ''

    return legalDescription.includes('township') ? 'township' : 'city'
  }

  const getTaxData = (state: string, expenses) => {
    if (state != 'FL' && state != 'VA') return {}

    const expensesTax =
      expenses.filter((expense) => expense.type === 'tax_and_other_government_fees') || []

    if (state == 'FL') {
      return {
        stampTax:
          expensesTax.find(
            (expenseTax) =>
              /(State).*(Tax\/Stamps)/i.test(expenseTax.name) ||
              expenseTax.name.toLowerCase() == 'state mortgage'
          )?.amount || 0,
        intangibleTax:
          expensesTax.find((expenseTax) =>
            ['intangible tax fee', 'intangible tax'].includes(expenseTax.name.toLowerCase())
          )?.amount || 0,
      }
    }

    return {
      recordationTax: expensesTax
        .filter((expenseTax) => /(State|County).*(Tax\/Stamps)/i.test(expenseTax.name))
        .reduce((partialSum, expense) => partialSum + parseFloat(expense.amount || 0), 0),
    }
  }

  const getTrustee = (state: string) => {
    switch (state) {
      case 'AZ':
      case 'NC':
      case 'NV':
      case 'WA':
        return 'ClearEdge Title, Inc'
      case 'CA':
        return 'Pacific Coast Title'
      case 'CO':
        return 'Public Trustee'
      case 'OR':
        return 'AmeriTitle'
      case 'TN':
        return 'Glenn Balletto'
      case 'UT':
        return 'Vantage Point Title'
      case 'VA':
        return 'Robert Pearsall'
      default:
        return 'None'
    }
  }

  const getOtherDocsQueryString = (appraisalReport, spousalConsent: boolean) => {
    let queryString = ''

    if (spousalConsent) queryString += `&otherDocs=spousalConsent`
    if (appraisalReport?.deferredMaintenanceItems != '') queryString += `&otherDocs=dma`

    return queryString
  }

  const removeHTMLTags = (text) => (text || '').replaceAll(/<[^>]*>/g, '')

  const docdrawLinkSearchParams = new URLSearchParams({
    ...getTaxData(property?.address?.state, application?.expenses || []),
    opportunityId: `${application?.outreachOpportunityId || ''}`,
    escrow_external_id: application?.titleReport?.fileNumber || '',
    effective_date: `${application?.agreementEffectiveOn || ''}`,
    trustee: getTrustee(property?.address?.state || ''),
    ownership_type: getOwnershipType(property?.titleVesting || ''),
    owner_counterparty_trust: property?.titleVesting || '',
    owner_counterparty_entity: property?.titleVesting || '',
    place_incorporation_type: getPropertyPlaceIncorporation(
      property?.address?.state || '',
      application?.titleReport?.legalDescription || ''
    ),
    addressIsMailing: applicant?.profile.mailing_address ? 'yes' : 'no',
    spouseName: application?.titleReport?.nooNameOfSpouse || '',
    dmaItems: removeHTMLTags(applicationValuationsCalcs?.appraisalReport?.deferredMaintenanceItems),
    property_legal_description: removeHTMLTags(application?.titleReport?.legalDescription),
    email: user.email || user.username || '',
    needs_payoffs: payoffs.find(
      (payoff) => payoff.payoff_amount != null && payoff.payoff_amount != '0.00'
    )
      ? 'true'
      : '',
    application_external_id: `${application?.externalId}`,
  })

  const aClasses =
    'hover:bg-slate-50 flex no-underline gap-1 text-sm 2xl:text-base items-center text-center p-4 hover:font-semibold'

  const downloadDocument = async (option: DownloadOptionType) => {
    if (!option.disabled) {
      if (downloadingDoc) {
        downloadingDoc(true)
      }

      const headers = localStorage.getItem('jwt')
        ? {
            'content-type': 'multipart/form-data',
            Authorization: `JWT ${localStorage.getItem('jwt')}`,
          }
        : {
            'content-type': 'multipart/form-data',
          }

      const { data } = await axios.get(option.downloadUrl, {
        headers,
        responseType: 'blob',
        withCredentials: true,
      })

      const file = new File([data], option.fileName, { type: 'application/pdf' })

      const docSnapshot = snapshots.filter(
        (snapshot) => snapshot.name == option.title.replaceAll('-', '_')
      )

      let payload = {
        application_profile: applicant.id,
        file_name: file.name,
        document: option.documentType,
        type: DOCUMENT_TYPE.APPLICATION,
        category: DOCUMENT_CATEGORY.DISCLOSURE,
        tag: DOCUMENT_TAG.DEAL_FILES,
        source: DOCUMENT_SOURCE.MAX_SYSTEM,
      }

      if (
        option.documentType === DOCUMENT_OPTIONS.CLOSING_STATEMENT ||
        option.documentType === DOCUMENT_OPTIONS.INVESTMENT_ESTIMATE
      ) {
        payload = {
          ...payload,
          type: DOCUMENT_TYPE.LEGAL,
          document:
            option.documentType === DOCUMENT_OPTIONS.CLOSING_STATEMENT
              ? DOCUMENT_OPTIONS.CLOSING_STATEMENT
              : DOCUMENT_OPTIONS.INVESTMENT_ESTIMATE,
        }
      }

      if (docSnapshot.length) {
        payload['snapshot'] = docSnapshot[0]['id']
      }

      const { url, uuid } = await documentUploadStart(payload).unwrap()
      await uploadFile({ url, file })
      const uploadedDoc = await documentUploadFinish(uuid).unwrap()
      openFilesInMaxViewer(application.externalId, [uploadedDoc])
      downloadFile(uploadedDoc)
      if (downloadingDoc) {
        downloadingDoc(false)
      }
    }
  }

  const dropdownMenuItems =
    application &&
    dropDownMenuItems(
      application,
      applicationProfiles?.results,
      showIEButton,
      showICSButton,
      applicationOffer,
      ApplicantSSN?.social_security_number,
      CoApplicantSSN?.social_security_number,
      snapshots,
      user,
      dispatch,
      downloadingDoc,
      documentUploadStart,
      documentUploadFinish,
      uploadFile,
      nonDisclosureContacts,
      isLocked,
      shouldHideDTI,
      alerts,
      lifeOfLoanFloodZoneCertFeatureFlag,
      hasOffer,
      generateSampleContractDocs
    )

  const handleDownloadFilesWithModal = async (option: DownloadOptionType) => {
    const { documentType } = option

    const existingDocs = userDocs.filter((it) => it.document === documentType)

    const docCount = existingDocs.length

    if (!docCount) {
      await downloadDocument(option)
    } else {
      setModalData({ option, docs: existingDocs })
      setShowingModal(true)
    }
  }

  return (
    <div
      className='flex-col gap-4 w-48 rounded border absolute bg-white -right-16 -top-3 shadow-lg'
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {showingModal && modalData?.option && (
        <DownloadSectionModal
          onClose={() => setShowingModal(false)}
          onDownload={async () => await downloadDocument(modalData?.option)}
          data={modalData}
        />
      )}

      {dropdownMenuItems?.map((item) => {
        return (
          <button
            key={item.title}
            className='group w-full'
            disabled={item.disabled}
          >
            <a
              className={`peer ${aClasses} ${item.title}-item ${
                isLocked && item.title === 'third-party' ? 'hover:!font-normal' : null
              }`}
              data-test={`${item.title}`}
            >
              <span className='w-5'>
                <Image
                  width={100}
                  height={100}
                  src={item.icon}
                  alt='icon'
                />
              </span>
              <span className='text-left text-sm'>{item.label}</span>
            </a>
            {item.options && (
              <div
                className='absolute bg-white w-48 right-[192px] hidden group-focus:inline hover:inline tooltip tooltip-left shadow-lg'
                style={{ top: `${item.top}rem` }}
                onMouseLeave={() => setActiveSubmenu(null)}
              >
                {item.title === 'documents' && (
                  <GenerateApplicationDocs
                    application={application}
                    applicationOffer={applicationOffer}
                    downloadingDoc={downloadingDoc}
                    onClose={(e) => {
                      setTimeout(() => {
                        setShowDropdown(false)
                        e.stopPropagation()
                      }, 0)
                    }}
                  />
                )}
                {item.options.map((option) => (
                  <div
                    key={`${item.title}-${option.title}`}
                    className='cursor-pointer relative'
                    data-test={`${item.title}-${option.title}`}
                    onMouseEnter={() => {
                      if (!option.subOptions) {
                        setActiveSubmenu(null)
                      }
                      if (option.disabledMessage) setDisabledMessage(option.disabledMessage)
                    }}
                    onMouseLeave={() => {
                      if (!option.subOptions && disabledMessage) setDisabledMessage(null)
                    }}
                    onClick={(e) => {
                      if (!option.subOptions) {
                        setShowDropdown(false)
                      }
                      e.stopPropagation()
                    }}
                  >
                    <div
                      className='w-full tooltip tooltip-left'
                      data-tip={option?.disabled ? option?.disabledMessage : null}
                    >
                      <a
                        className={aClasses}
                        style={option.disabled ? disableLinkStyle : undefined}
                        target={option.href && '_blank'}
                        href={
                          option.disabled
                            ? undefined
                            : option.href
                              ? String(option.href)
                              : undefined
                        }
                        data-test={`${option.title}-download-button`}
                        onClick={
                          option.subOptions
                            ? (e) => {
                                e.preventDefault()
                                setActiveSubmenu(
                                  activeSubmenu === option.title ? null : option.title
                                )
                              }
                            : option.onClick
                              ? () =>
                                  option.functionClick ? option.onClick() : dispatch(option.onClick)
                              : () => {
                                  option.downloadUrl && handleDownloadFilesWithModal(option)
                                }
                        }
                      >
                        <span className='w-5'>
                          <Image
                            width={100}
                            height={100}
                            src={item.icon}
                            alt='icon'
                          />
                        </span>
                        <span className='text-left w-10/12 text-sm'>{option.label}</span>
                      </a>
                    </div>
                    {option.subOptions && activeSubmenu === option.title && (
                      <div
                        className='absolute bg-white w-48 left-full top-0 shadow-md rounded border z-10'
                        onMouseEnter={() => setActiveSubmenu(option.title)}
                      >
                        {option.subOptions.map((subOption) => (
                          <div
                            key={`${option.title}-${subOption.title}`}
                            className='cursor-pointer'
                            data-test={`${option.title}-${subOption.title}-subitem`}
                            onClick={(e) => {
                              setShowDropdown(false)
                              e.stopPropagation()
                            }}
                          >
                            <div
                              className='w-full tooltip tooltip-left'
                              data-tip={subOption?.disabled ? subOption?.disabledMessage : null}
                            >
                              <a
                                className={aClasses}
                                style={subOption.disabled ? disableLinkStyle : undefined}
                                target={subOption.href && '_blank'}
                                href={
                                  subOption.disabled
                                    ? undefined
                                    : subOption.href
                                      ? String(subOption.href)
                                      : undefined
                                }
                                data-test={`${subOption.title}-download-button`}
                                onClick={
                                  subOption.onClick
                                    ? () =>
                                        subOption.functionClick
                                          ? subOption.onClick()
                                          : dispatch(subOption.onClick)
                                    : () => {
                                        subOption.downloadUrl &&
                                          handleDownloadFilesWithModal(subOption)
                                      }
                                }
                              >
                                <span className='w-5'>
                                  <Image
                                    width={100}
                                    height={100}
                                    src={item.icon}
                                    alt='icon'
                                  />
                                </span>
                                <span className='text-left w-10/12 text-sm'>{subOption.label}</span>
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </button>
        )
      })}
    </div>
  )
}
